import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Package,
  Settings,
  ListChecks,
  Play,
  Heart,
  Lightbulb,
  Info,
  ShieldAlert,
  ExternalLink,
} from 'lucide-react';
import { getProcedureById, procedures, ProcedureData } from '@/data/procedures';

const sectionIcons: Record<string, React.ReactNode> = {
  overview: <Info className="w-4 h-4" />,
  supplies: <Package className="w-4 h-4" />,
  setup: <Settings className="w-4 h-4" />,
  steps: <ListChecks className="w-4 h-4" />,
  videos: <Play className="w-4 h-4" />,
  aftercare: <Heart className="w-4 h-4" />,
  pearls: <Lightbulb className="w-4 h-4" />,
};

function Section({
  id,
  icon,
  title,
  accentClass,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  accentClass: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mb-6">
      <div className={`flex items-center gap-2 mb-3 pb-2 border-b border-border`}>
        <span className={`p-1.5 rounded-md ${accentClass}`}>{icon}</span>
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-foreground/90 leading-snug">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/50 flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StepList({ items }: { items: string[] }) {
  return (
    <ol className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-sm">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
            {i + 1}
          </span>
          <span className="text-foreground/90 leading-snug pt-0.5">{item}</span>
        </li>
      ))}
    </ol>
  );
}

function getYouTubeId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?]+)/);
  return match ? match[1] : null;
}

export default function ProcedurePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const procedure = getProcedureById(id || '');

  if (!procedure) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-24">
        <p className="text-muted-foreground text-lg mb-4">Procedure not found.</p>
        <Link to="/" className="text-primary text-sm underline">
          Return to Home
        </Link>
      </div>
    );
  }

  // Get siblings for prev/next navigation
  const sameCat = procedures.filter((p) => p.categoryId === procedure.categoryId);
  const currentIdx = sameCat.findIndex((p) => p.id === procedure.id);
  const prevProc = currentIdx > 0 ? sameCat[currentIdx - 1] : null;
  const nextProc = currentIdx < sameCat.length - 1 ? sameCat[currentIdx + 1] : null;

  const categoryColorMap: Record<string, string> = {
    msk: 'bg-blue-100 text-blue-700',
    derm: 'bg-amber-100 text-amber-700',
    womens: 'bg-rose-100 text-rose-700',
    audiology: 'bg-violet-100 text-violet-700',
  };
  const catColor = categoryColorMap[procedure.categoryId] || 'bg-muted text-muted-foreground';

  return (
    <motion.div
      key={id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-16"
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground transition-colors">
          Home
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catColor}`}>
          {procedure.category}
        </span>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-foreground/70 truncate">{procedure.title}</span>
      </div>

      {/* Page title */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight mb-1">
          {procedure.title}
        </h1>
        <p className="text-muted-foreground text-sm">{procedure.category} · Family Medicine Outpatient Procedure</p>
      </div>

      {/* ─── OVERVIEW ─── */}
      <Section id="overview" icon={sectionIcons.overview} title="Overview" accentClass="bg-sky-100 text-sky-700">
        <p className="text-sm text-foreground/90 leading-relaxed mb-4">{procedure.overview.description}</p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg border border-border p-4 bg-card">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Common Indications
            </h3>
            <BulletList items={procedure.overview.indications} />
          </div>
          <div className="rounded-lg border border-destructive/20 p-4 bg-destructive/5">
            <div className="flex items-center gap-1.5 mb-2">
              <ShieldAlert className="w-3.5 h-3.5 text-destructive" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-destructive">
                Contraindications
              </h3>
            </div>
            <BulletList items={procedure.overview.contraindications} />
          </div>
        </div>
      </Section>

      {/* ─── SUPPLIES ─── */}
      <Section id="supplies" icon={sectionIcons.supplies} title="Supplies Needed" accentClass="bg-emerald-100 text-emerald-700">
        <div className="rounded-lg border border-border bg-card p-4">
          <BulletList items={procedure.supplies} />
        </div>
      </Section>

      {/* ─── SETUP / POSITIONING ─── */}
      <Section id="setup" icon={sectionIcons.setup} title="Setup / Positioning" accentClass="bg-orange-100 text-orange-700">
        <div className="rounded-lg border border-border bg-card p-4">
          <BulletList items={procedure.setup} />
        </div>
      </Section>

      {/* ─── STEP BY STEP ─── */}
      <Section id="steps" icon={sectionIcons.steps} title="Step-by-Step Procedure" accentClass="bg-primary/10 text-primary">
        <div className="rounded-lg border border-border bg-card p-4">
          <StepList items={procedure.steps} />
        </div>
      </Section>

      {/* ─── VIDEOS ─── */}
      <Section id="videos" icon={sectionIcons.videos} title="Video Resources" accentClass="bg-red-100 text-red-700">
        <div className="space-y-4">
          {procedure.videos.map((video, i) => {
            const ytId = getYouTubeId(video.url);
            const isYouTube = !!ytId;

            return (
              <div key={i} className="rounded-lg border border-border bg-card overflow-hidden">
                {isYouTube ? (
                  <div className="aspect-video w-full bg-black">
                    <iframe
                      src={`https://www.youtube.com/embed/${ytId}`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                ) : null}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-foreground leading-snug">{video.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{video.channel}</div>
                      {video.note && (
                        <div className="text-xs text-muted-foreground/80 mt-1 italic">{video.note}</div>
                      )}
                    </div>
                    <a
                      href={video.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 flex items-center gap-1 text-xs text-primary border border-primary/30 px-2.5 py-1 rounded-md hover:bg-primary/8 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open
                    </a>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Butterfly Network links (POCUS sections) */}
          {procedure.butterflyLinks && procedure.butterflyLinks.length > 0 && (
            <div className="rounded-lg border border-teal-200 bg-teal-50 p-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-teal-800">🦋 Butterfly Network Educational Modules</span>
                <span className="text-xs text-teal-600 font-normal">(Butterfly Cloud login required)</span>
              </div>
              <ul className="space-y-2">
                {procedure.butterflyLinks.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-teal-700 hover:text-teal-900 hover:underline transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Section>

      {/* ─── AFTERCARE ─── */}
      <Section id="aftercare" icon={sectionIcons.aftercare} title="Aftercare / Counseling" accentClass="bg-pink-100 text-pink-700">
        <div className="rounded-lg border border-border bg-card p-4">
          <BulletList items={procedure.aftercare} />
        </div>
      </Section>

      {/* ─── PEARLS ─── */}
      <Section id="pearls" icon={sectionIcons.pearls} title="Resident Pearls & Pitfalls" accentClass="bg-yellow-100 text-yellow-700">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <ul className="space-y-2.5">
            {procedure.pearls.map((pearl, i) => (
              <li key={i} className="text-sm text-foreground/90 leading-snug">
                {pearl}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Prev / Next navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-border gap-3">
        {prevProc ? (
          <button
            onClick={() => navigate(`/procedure/${prevProc.id}`)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            <div className="text-left">
              <div className="text-xs text-muted-foreground">Previous</div>
              <div className="font-medium text-foreground/80 group-hover:text-foreground">{prevProc.title}</div>
            </div>
          </button>
        ) : (
          <div />
        )}
        {nextProc ? (
          <button
            onClick={() => navigate(`/procedure/${nextProc.id}`)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group ml-auto"
          >
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Next</div>
              <div className="font-medium text-foreground/80 group-hover:text-foreground">{nextProc.title}</div>
            </div>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        ) : (
          <div />
        )}
      </div>
    </motion.div>
  );
}
